import {useState} from 'react';
import {MODAL_SEVERITY, TASK_ACTIONS,} from 'utils/constants';
import {Modal} from "components/modals/Modal";
import DeleteAttachment from "components/modals/DeleteAttachment.modal";
import ErrorModal from "components/modals/Error.modal";

import { useDispatch, useSelector } from "react-redux";
import {actionFinished} from 'components/mytasks/action/taskAction.action';
import {showNotification} from "components/notifications/notification.action";
import {useTranslation} from "components/translations/useTranslation";
import * as logger from 'utils/logger.function';
import * as reduxSelectors from "components/store/application.reducers";
import { Store } from "redux";

/**
 * asynchronous part of the task action - showing the dialog, which will execute the action
 * synchronous part of action is inside redux action
 *
 * this window is a portal into a popped-out browser window, so it mounts the modal itself rather
 * than going through ModalContext - the shared ModalRouter would render it in the main window
 */
export const AsyncTaskActionExecutor = () => {
    const dispatch = useDispatch();
    const {translate} = useTranslation();
    const tasks = useSelector((store: Store) => reduxSelectors.getTasksForRender(store));
    const actionParameters = useSelector((store: Store) => reduxSelectors.getActionParameters(store));
    // the router swaps the open modal for an error one; without it we hold the error here
    const [error, setError] = useState<{ error: unknown } | null>(null);

    const closeModal = () => dispatch(actionFinished());

    // dismissing the error also finishes the action, otherwise the delete modal comes straight back
    const dismissError = () => {
        setError(null);
        closeModal();
    };

    if (error) {
        return (
            <Modal
                open={true}
                onClose={dismissError}
                title={translate("popUp.error.title")}
                severity={MODAL_SEVERITY.DANGER}
            >
                {({loopFocus, initialFocusRef}) => (
                    <ErrorModal
                        more={error}
                        closeModal={dismissError}
                        loopFocus={loopFocus}
                        initialFocusRef={initialFocusRef}
                    />
                )}
            </Modal>
        );
    }

    // nothing to show
    if (!actionParameters?.type) {
        return null;
    }

    const taskList = tasks.allTasks.map((task: any) => task.task);

    const tasksForAction = taskList
        .filter((task : any) => { // find those matching props.taskIds
            return actionParameters.taskIds.some((taskKeyWanted: any) => {
                return taskKeyWanted === task.key;
            });
        });

    /**
     * sometimes, when user has dialog opened and navigates away for some time or changes context
     * the keys for task/history details expire, which then results in empty tasks for the popup
     * so detect this situation and rather hide the popup than check for invalid props inside each popup
     */
    if (tasksForAction.length === 0) {
        logger.debug("AsyncTaskActionExecutor", "no task to be shown for these keys: " + actionParameters.taskIds);
        dispatch(actionFinished());
        return null;
    }

    const title = translate("popUp.deleteAttachment.title");

    const more = {
        title,
        closeCallback: closeModal,
        tasks: tasksForAction,
        showNotification: (data: string) => dispatch(showNotification(data)),
    };

    // according to type either show the modal or execute the action directly ..
    if (actionParameters.type === TASK_ACTIONS.DELETE_ATTACHMENT) {
        return (
            <Modal
                open={true}
                onClose={closeModal}
                title={title}
                description={translate("popUp.deleteAttachment.message")}
                severity={MODAL_SEVERITY.WARNING}
            >
                {({loopFocus, initialFocusRef}) => (
                    <DeleteAttachment
                        more={more}
                        closeModal={closeModal}
                        loopFocus={loopFocus}
                        initialFocusRef={initialFocusRef}
                        errorCallback={(raised) => setError(raised as { error: unknown })}
                    />
                )}
            </Modal>
        );
    } else {
        console.log('unsupported action type: ' + actionParameters.type);
        return null;
    }

}

export default AsyncTaskActionExecutor;
